import { ChannelIcon, Dialog, Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, BookOpen, Pencil, Plus, RotateCcw, Send, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Field } from '../crm/forms';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTeamName, useTeams } from '../pages/Teams';
import { DialogButtons } from '../retail/ui';

const CHANNELS = ['whatsapp', 'instagram', 'facebook', 'tiktok', 'webchat', 'email', 'voice'] as const;
const TOPICS = ['delivery', 'payment', 'hours', 'returns', 'location', 'general'] as const;

interface Settings {
  enabled: boolean;
  name: string;
  channels: string[];
  schedule: 'always' | 'outside_hours';
  takeOrders: boolean;
  answerCalls: boolean;
  answerComments: boolean;
  handoffTeamId: string | null;
  maxTurns: number;
}
interface Knowledge {
  id: string;
  topic: (typeof TOPICS)[number];
  question: string;
  answer: string;
  answerAr: string | null;
  keywords: string[];
  enabled: boolean;
}
interface Overview {
  settings: Settings;
  knowledge: Knowledge[];
  stats: { conversations: number; replies: number; handedOff: number; orders: number; revenueMinor: string };
  model: 'configured' | 'rules';
}
interface Activity {
  id: string;
  action: 'replied' | 'order_proposed' | 'order_recorded' | 'waitlisted' | 'handed_off' | 'failed';
  engine: 'rules' | 'model';
  detail: Record<string, unknown>;
  at: string;
  conversationId: string | null;
  channel: string | null;
  personName: string | null;
}

/**
 * The AI assistant: switch it on, choose where and when it answers, teach it about the business, try it, and see
 * what it did. It answers customers and takes orders by itself, and hands over to the team when it should.
 */
export function AssistantPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { session } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['assistant'], queryFn: () => api<Overview>('/v1/assistant') });
  const [draft, setDraft] = useState<Settings | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data.settings);
  }, [q.data]);
  const save = useMutation({
    mutationFn: (s: Partial<Settings>) =>
      api<Settings>('/v1/assistant/settings', { method: 'PUT', body: JSON.stringify(s) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['assistant'] }),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data || !draft) return <ErrorState error={q.error} />;
  const o = q.data;
  const dirty = JSON.stringify(draft) !== JSON.stringify(o.settings);
  const set = (p: Partial<Settings>) => setDraft({ ...draft, ...p });

  return (
    <>
      <PageHead
        eyebrow={t('nav.admin')}
        title={t('assistant.page.title')}
        subtitle={t('assistant.page.subtitle')}
      />
      <section
        className={`card card-pad assistant-hero${o.settings.enabled ? ' is-on' : ''}`}
        data-testid="assistant-status"
      >
        <div className="assistant-hero-main">
          <span className="assistant-avatar" aria-hidden="true">
            <Bot size={22} />
          </span>
          <div className="grow">
            <h2 className="section-title" style={{ margin: 0 }}>
              {o.settings.name} <span className="ai-pill">{t('assistant.ai')}</span>
            </h2>
            <p className="faint" style={{ margin: '0.2rem 0 0' }}>
              {o.settings.enabled ? t('assistant.page.on') : t('assistant.page.off')} ·{' '}
              {o.model === 'configured' ? t('assistant.page.modelOn') : t('assistant.page.modelRules')}
            </p>
          </div>
          <label className="toggle toggle-lg">
            <input
              type="checkbox"
              role="switch"
              checked={o.settings.enabled}
              disabled={save.isPending}
              onChange={(e) => save.mutate({ enabled: e.target.checked })}
              aria-label={t('assistant.page.switch')}
              data-testid="assistant-toggle"
            />
            <span aria-hidden="true" />
          </label>
        </div>
        <dl className="assistant-stats">
          <div>
            <dt>{t('assistant.page.stats.conversations')}</dt>
            <dd>{fmt.number(o.stats.conversations)}</dd>
          </div>
          <div>
            <dt>{t('assistant.page.stats.orders')}</dt>
            <dd>
              {fmt.number(o.stats.orders)}
              <span className="faint">
                {' '}
                · {fmt.money(Number(o.stats.revenueMinor), session!.tenant.currency)}
              </span>
            </dd>
          </div>
          <div>
            <dt>{t('assistant.page.stats.handedOff')}</dt>
            <dd>{fmt.number(o.stats.handedOff)}</dd>
          </div>
        </dl>
        <p className="faint" style={{ margin: 0, fontSize: 'var(--t-xs)' }}>
          {t('assistant.page.statsWindow')}
        </p>
      </section>

      <div className="assistant-grid">
        <div className="assistant-col">
          <section className="card card-pad" data-testid="assistant-settings">
            <h2 className="section-title">{t('assistant.page.settings')}</h2>
            <div className="form-grid">
              <Field id="as-name" label={t('assistant.page.name')}>
                <input
                  id="as-name"
                  className="input"
                  maxLength={60}
                  value={draft.name}
                  onChange={(e) => set({ name: e.target.value })}
                />
              </Field>
              <Field id="as-schedule" label={t('assistant.page.when')}>
                <Select
                  id="as-schedule"
                  value={draft.schedule}
                  onChange={(v) => set({ schedule: v as Settings['schedule'] })}
                  options={(['always', 'outside_hours'] as const).map((v) => ({
                    value: v,
                    label: t(`assistant.page.schedule.${v}`),
                  }))}
                />
              </Field>
            </div>
            <div className="as-channels">
              <span className="as-label">{t('assistant.page.channels')}</span>
              <div className="chips" role="group" aria-label={t('assistant.page.channels')}>
                {CHANNELS.map((c) => (
                  <label key={c} className="chip chip-toggle">
                    <input
                      type="checkbox"
                      checked={draft.channels.includes(c)}
                      onChange={(e) =>
                        set({
                          channels: e.target.checked
                            ? [...draft.channels, c]
                            : draft.channels.filter((x) => x !== c),
                        })
                      }
                      data-testid={`as-channel-${c}`}
                    />
                    <ChannelIcon channel={c} size={11} />
                    {c === 'voice' ? t('assistant.page.phoneCalls') : t(`channels.${c}`)}
                  </label>
                ))}
              </div>
            </div>
            <div className="switch-list">
              <SwitchRow
                label={t('assistant.page.takeOrders')}
                hint={t('assistant.page.takeOrdersHint')}
                checked={draft.takeOrders}
                onChange={(v) => set({ takeOrders: v })}
                testId="as-take-orders"
              />
              <SwitchRow
                label={t('assistant.page.answerCalls')}
                hint={t('assistant.page.answerCallsHint')}
                checked={draft.answerCalls}
                onChange={(v) =>
                  set({
                    answerCalls: v,
                    channels:
                      v && !draft.channels.includes('voice') ? [...draft.channels, 'voice'] : draft.channels,
                  })
                }
                testId="as-answer-calls"
              />
            </div>
            <div className="form-grid">
              <Field
                id="as-team"
                label={t('assistant.page.handoffTeam')}
                hint={t('assistant.page.handoffHint')}
              >
                <TeamSelect
                  id="as-team"
                  value={draft.handoffTeamId}
                  onChange={(v) => set({ handoffTeamId: v })}
                />
              </Field>
              <Field
                id="as-turns"
                label={t('assistant.page.maxTurns')}
                hint={t('assistant.page.maxTurnsHint')}
              >
                <input
                  id="as-turns"
                  className="input"
                  type="number"
                  min={2}
                  max={30}
                  value={draft.maxTurns}
                  onChange={(e) => set({ maxTurns: Math.max(2, Math.min(30, Number(e.target.value) || 8)) })}
                />
              </Field>
            </div>
            {save.isError && <ErrorState error={save.error} />}
            <div className="form-actions">
              <button className="btn btn-ghost" disabled={!dirty} onClick={() => setDraft(o.settings)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-grad"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate(draft)}
                data-testid="as-save"
              >
                {t('common.save')}
              </button>
            </div>
          </section>
          <KnowledgeCard items={o.knowledge} />
        </div>
        <div className="assistant-col">
          <TryIt name={o.settings.name} />
          <ActivityCard />
        </div>
      </div>
    </>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  onChange,
  testId,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <label className="switch-row">
      <span className="grow">
        <strong>{label}</strong>
        <span className="faint">{hint}</span>
      </span>
      <span className="toggle">
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
        />
        <span aria-hidden="true" />
      </span>
    </label>
  );
}

function TeamSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const teams = useTeams();
  const teamName = useTeamName();
  return (
    <Select
      id={id}
      value={value ?? ''}
      onChange={(v) => onChange(v || null)}
      options={[
        { value: '', label: t('assistant.page.sameTeam') },
        ...(teams.data?.items ?? []).map((x) => ({ value: x.id, label: teamName(x) })),
      ]}
    />
  );
}

// ------------------------------------------------------------------------------------------------- knowledge

function KnowledgeCard({ items }: { items: Knowledge[] }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Knowledge | 'new' | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/assistant/knowledge/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['assistant'] }),
  });
  const ar = i18n.language === 'ar';
  return (
    <section className="card card-pad" data-testid="assistant-knowledge">
      <div className="card-head-row">
        <h2 className="section-title" style={{ margin: 0 }}>
          <BookOpen size={16} aria-hidden="true" /> {t('assistant.knowledge.title')}
        </h2>
        <button className="btn btn-sm" onClick={() => setEditing('new')} data-testid="add-knowledge">
          <Plus size={14} />
          {t('assistant.knowledge.add')}
        </button>
      </div>
      <p className="faint" style={{ marginTop: '0.25rem' }}>
        {t('assistant.knowledge.hint')}
      </p>
      {!items.length ? (
        <Empty text={t('assistant.knowledge.empty')} />
      ) : (
        <ul className="list-plain knowledge-list">
          {items.map((k) => (
            <li key={k.id} className={k.enabled ? undefined : 'is-off'}>
              <div className="grow">
                <div className="knowledge-q">
                  <span className="badge badge-neutral">{t(`assistant.topic.${k.topic}`)}</span>
                  <strong dir="auto">{k.question}</strong>
                </div>
                <p className="faint" dir="auto">
                  {(ar && k.answerAr) || k.answer}
                </p>
              </div>
              <button className="icon-btn" aria-label={t('common.edit')} onClick={() => setEditing(k)}>
                <Pencil size={14} />
              </button>
              <button
                className="icon-btn"
                aria-label={t('common.delete')}
                onClick={() => remove.mutate(k.id)}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <KnowledgeDialog item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function KnowledgeDialog({ item, onClose }: { item: Knowledge | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState({
    topic: item?.topic ?? 'general',
    question: item?.question ?? '',
    answer: item?.answer ?? '',
    answerAr: item?.answerAr ?? '',
    keywords: item?.keywords.join(', ') ?? '',
  });
  const save = useMutation({
    mutationFn: () =>
      api(item ? `/v1/assistant/knowledge/${item.id}` : '/v1/assistant/knowledge', {
        method: item ? 'PATCH' : 'POST',
        body: JSON.stringify({
          topic: f.topic,
          question: f.question,
          answer: f.answer,
          answerAr: f.answerAr.trim() || null,
          keywords: f.keywords
            .split(/[,،]/)
            .map((k) => k.trim())
            .filter(Boolean),
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['assistant'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      title={item ? t('assistant.knowledge.edit') : t('assistant.knowledge.add')}
      onClose={onClose}
      onSubmit={() => save.mutate()}
      testId="knowledge-dialog"
      footer={
        <DialogButtons
          onClose={onClose}
          busy={save.isPending}
          disabled={!f.question.trim() || !f.answer.trim()}
          label={t('common.save')}
        />
      }
    >
      <Field id="kn-topic" label={t('assistant.knowledge.topic')}>
        <Select
          id="kn-topic"
          value={f.topic}
          onChange={(v) => setF({ ...f, topic: v as Knowledge['topic'] })}
          options={TOPICS.map((x) => ({ value: x, label: t(`assistant.topic.${x}`) }))}
        />
      </Field>
      <Field id="kn-q" label={t('assistant.knowledge.question')}>
        <input
          id="kn-q"
          className="input"
          required
          value={f.question}
          onChange={(e) => setF({ ...f, question: e.target.value })}
        />
      </Field>
      <Field id="kn-a" label={t('assistant.knowledge.answer')}>
        <textarea
          id="kn-a"
          className="textarea"
          rows={3}
          required
          value={f.answer}
          onChange={(e) => setF({ ...f, answer: e.target.value })}
        />
      </Field>
      <Field id="kn-ar" label={t('assistant.knowledge.answerAr')}>
        <textarea
          id="kn-ar"
          className="textarea"
          rows={3}
          dir="rtl"
          lang="ar"
          value={f.answerAr}
          onChange={(e) => setF({ ...f, answerAr: e.target.value })}
        />
      </Field>
      <Field id="kn-k" label={t('assistant.knowledge.keywords')} hint={t('assistant.knowledge.keywordsHint')}>
        <input
          id="kn-k"
          className="input"
          value={f.keywords}
          onChange={(e) => setF({ ...f, keywords: e.target.value })}
        />
      </Field>
      {save.isError && <ErrorState error={save.error} />}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------- try it

interface TryLine {
  from: 'customer' | 'assistant';
  text: string;
  note?: string;
}

function TryIt({ name }: { name: string }) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<TryLine[]>([]);
  const [state, setState] = useState<Record<string, unknown>>({});
  const [text, setText] = useState('');
  const ask = useMutation({
    mutationFn: (msg: string) =>
      api<{
        reply: string;
        actions: { type: string; reason?: string; lines?: unknown[] }[];
        state: Record<string, unknown>;
        engine: string;
      }>('/v1/assistant/try', {
        method: 'POST',
        body: JSON.stringify({
          text: msg,
          state,
          history: lines.map(({ from, text: x }) => ({ from, text: x })),
        }),
      }),
    onSuccess: (r, msg) => {
      const note = r.actions
        .map((a) =>
          a.type === 'handoff'
            ? t(`assistant.try.handoff`, { reason: t(`assistant.reason.${a.reason}`) })
            : t(`assistant.try.${a.type}`),
        )
        .join(' · ');
      setLines((ls) => [
        ...ls,
        { from: 'customer', text: msg },
        { from: 'assistant', text: r.reply, note: note || undefined },
      ]);
      setState(r.state);
      setText('');
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim()) ask.mutate(text.trim());
  };
  return (
    <section className="card card-pad try-it" data-testid="assistant-try">
      <div className="card-head-row">
        <h2 className="section-title" style={{ margin: 0 }}>
          {t('assistant.try.title')}
        </h2>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setLines([]);
            setState({});
          }}
          disabled={!lines.length}
        >
          <RotateCcw size={14} />
          {t('assistant.try.reset')}
        </button>
      </div>
      <p className="faint" style={{ marginTop: '0.25rem' }}>
        {t('assistant.try.hint')}
      </p>
      <div className="try-thread" aria-live="polite">
        {!lines.length && <p className="faint try-empty">{t('assistant.try.example')}</p>}
        {lines.map((l, i) => (
          <div key={i} className={`msg-group ${l.from === 'customer' ? 'in' : 'out'}`}>
            <div className="msg-stack">
              {l.from === 'assistant' && (
                <div className="msg-author">
                  {name} <span className="ai-pill">{t('assistant.ai')}</span>
                </div>
              )}
              <div
                className={`bubble ${l.from === 'customer' ? 'in' : 'out'}`}
                dir="auto"
                data-testid={l.from === 'assistant' ? 'try-reply' : undefined}
              >
                {l.text}
              </div>
              {l.note && (
                <div className="try-note" data-testid="try-note">
                  {l.note}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <form className="try-form" onSubmit={submit}>
        <input
          className="input"
          dir="auto"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('assistant.try.placeholder')}
          aria-label={t('assistant.try.placeholder')}
          data-testid="try-input"
        />
        <button
          className="btn btn-grad"
          disabled={!text.trim() || ask.isPending}
          aria-label={t('assistant.try.send')}
        >
          <Send size={15} />
        </button>
      </form>
      {ask.isError && <ErrorState error={ask.error} />}
    </section>
  );
}

// -------------------------------------------------------------------------------------------------- activity

function ActivityCard() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const q = useQuery({
    queryKey: ['assistant', 'activity'],
    queryFn: () => api<{ items: Activity[] }>('/v1/assistant/activity?limit=40'),
    refetchInterval: 15_000,
  });
  const items = q.data?.items ?? [];
  return (
    <section className="card card-pad" data-testid="assistant-activity">
      <h2 className="section-title">{t('assistant.activity.title')}</h2>
      {q.isLoading && <Loading />}
      {q.data && !items.length && <Empty text={t('assistant.activity.empty')} />}
      <ul className="list-plain activity-list">
        {items.map((a) => (
          <li key={a.id}>
            {a.channel && <ChannelIcon channel={a.channel} size={12} />}
            <span className="grow" dir="auto">
              {t(`assistant.activity.${a.action}`, {
                name: a.personName ?? '—',
                number: String(a.detail.number ?? ''),
                reason: t(`assistant.reason.${String(a.detail.reason ?? 'not_understood')}`),
              })}
              {a.conversationId && (
                <>
                  {' '}
                  <Link to={`/inbox?c=${a.conversationId}`}>{t('assistant.activity.open')}</Link>
                </>
              )}
            </span>
            <time className="faint" dateTime={a.at} title={fmt.dateTime(a.at)}>
              {fmt.relative(a.at)}
            </time>
          </li>
        ))}
      </ul>
    </section>
  );
}
